<template>
  <div class="app-autocomplete" ref="autocompleteRef">
    <input
      type="text"
      class="app-input"
      :value="search"
      @input="onInput"
      @keydown.down.prevent="onArrowDown"
      @keydown.up.prevent="onArrowUp"
      @keydown.enter.prevent="onEnter"
      @focus="onFocus"
      @blur="onBlur"
      v-bind="$attrs"
      autocomplete="off"
    />
    <ul v-show="isOpen" class="app-autocomplete__results">
      <li v-if="isLoading" class="app-autocomplete__result app-autocomplete__result--loading">
        Cargando...
      </li>
      <li
        v-else
        v-for="(result, i) in filteredResults"
        :key="i"
        @click="setResult(result)"
        class="app-autocomplete__result"
        :class="{ 'app-autocomplete__result--active': i === arrowCounter }"
      >
        <slot name="item" :item="result">
          {{ getItemText(result) }}
        </slot>
      </li>
      <li v-if="!isLoading && filteredResults.length === 0" class="app-autocomplete__result app-autocomplete__result--empty">
        <slot name="empty">No hay resultados</slot>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  value: {
    type: [String, Object, Number],
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  itemText: {
    type: String,
    default: 'text'
  },
  itemValue: {
    type: String,
    default: 'value'
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['input', 'select'])

const isOpen = ref(false)
const search = ref('')
const arrowCounter = ref(-1)

const getItemText = (item) => {
  if (item === null || item === undefined) return ''
  if (typeof item === 'object') {
    return item[props.itemText] || ''
  }
  return String(item)
}

const findItemByValue = (val) => {
  if (val === null || val === undefined || val === '') return null
  return props.items.find(item => {
    if (typeof item === 'object') {
      return item[props.itemValue] === val
    }
    return item === val
  }) || null
}

watch(() => props.value, (newVal) => {
  // If the value perfectly matches an item, set search to its text.
  // Otherwise, only clear the search text if the parent explicitly resets it
  // (we know it's a reset if search.value already matched a valid item).
  const item = findItemByValue(newVal)
  if (item !== null) {
    const text = getItemText(item)
    if (search.value !== text) {
      search.value = text
    }
  } else if (!newVal) {
    const currentSearch = search.value ? search.value.toString().toLowerCase() : ''
    const currentExactMatch = props.items.find(i => getItemText(i).toString().toLowerCase() === currentSearch)
    if (currentExactMatch || search.value === '') {
      search.value = ''
    }
  }
}, { immediate: true })

const filteredResults = computed(() => {
  if (!search.value) return props.items
  const lowerSearch = search.value.toString().toLowerCase()
  return props.items.filter(item => {
    const text = getItemText(item).toString().toLowerCase()
    return text.includes(lowerSearch)
  })
})

const onInput = (event) => {
  search.value = event.target.value
  arrowCounter.value = -1
  isOpen.value = true
  
  const currentSearch = search.value ? search.value.toString().toLowerCase() : ''
  const exactMatch = props.items.find(item => getItemText(item).toString().toLowerCase() === currentSearch)
  
  if (exactMatch) {
    const emittedValue = typeof exactMatch === 'object' ? exactMatch[props.itemValue] : exactMatch
    emit('input', emittedValue)
  } else {
    emit('input', null)
  }
}

const onFocus = () => {
  isOpen.value = true
  arrowCounter.value = -1
}

const setResult = (result) => {
  search.value = getItemText(result)
  isOpen.value = false
  arrowCounter.value = -1
  const emittedValue = typeof result === 'object' ? result[props.itemValue] : result
  emit('input', emittedValue)
  emit('select', result)
}

const onBlur = () => {
  // Using timeout to allow 'click' on the list item to register first
  setTimeout(() => {
    const currentSearch = search.value ? search.value.toString().toLowerCase() : ''
    const exactMatch = props.items.find(item => getItemText(item).toString().toLowerCase() === currentSearch)

    if (exactMatch) {
      setResult(exactMatch)
    } else {
      search.value = ''
      emit('input', null)
      emit('select', null)
    }

    isOpen.value = false
    arrowCounter.value = -1
  }, 150)
}

const onArrowDown = () => {
  if (!isOpen.value) {
    isOpen.value = true
    return
  }
  if (arrowCounter.value < filteredResults.value.length - 1) {
    arrowCounter.value++
  }
}

const onArrowUp = () => {
  if (arrowCounter.value > 0) {
    arrowCounter.value--
  }
}

const onEnter = () => {
  if (isOpen.value && arrowCounter.value >= 0 && arrowCounter.value < filteredResults.value.length) {
    setResult(filteredResults.value[arrowCounter.value])
  } else if (isOpen.value) {
    isOpen.value = false
  }
}
</script>

<script>
export default {
  inheritAttrs: false // To bind attrs correctly to the input element
}
</script>

<style scoped>
.app-autocomplete {
  position: relative;
  width: 100%;
}
.app-input {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: var(--app-font-size-base, 1rem);
  font-weight: 400;
  line-height: var(--app-line-height, 1.5);
  color: var(--app-text-color, #495057);
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid var(--app-border-color, #ced4da);
  border-radius: var(--app-border-radius, 0.25rem);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.app-input:focus {
  color: var(--app-text-color, #495057);
  background-color: #fff;
  border-color: var(--app-primary);
  outline: 0;
  box-shadow: 0 0 0 0.2rem var(--app-primary-focus, rgba(15, 40, 53, 0.25));
}
.app-input:disabled, .app-input[readonly] {
  background-color: #e9ecef;
  opacity: 1;
}

.app-autocomplete__results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 0.25rem;
  padding: 0;
  background-color: #fff;
  border: 1px solid var(--app-border-color, #ced4da);
  border-radius: var(--app-border-radius, 0.25rem);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
}

.app-autocomplete__result {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: var(--app-text-color, #212529);
  transition: background-color 0.1s ease;
}

.app-autocomplete__result:hover,
.app-autocomplete__result--active {
  background-color: var(--app-primary, #e9ecef);
  color: #fff;
}

.app-autocomplete__result--loading,
.app-autocomplete__result--empty {
  color: #6c757d;
  cursor: default;
  font-style: italic;
}
.app-autocomplete__result--loading:hover,
.app-autocomplete__result--empty:hover,
.app-autocomplete__result--loading.app-autocomplete__result--active,
.app-autocomplete__result--empty.app-autocomplete__result--active {
  background-color: #fff;
  color: #6c757d;
}
</style>
